import { Typography } from '@execonline-inc/execonline-ui';
import { fromEmpty } from 'maybeasy';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { T } from '../../../../../../Translations';
import { UnlicensedAccess } from '../HiddenCourseTreatment';
import { LicenseAlerts } from '../LicenseAlertsStore/Types';
import {
  AccessLevel,
  ALL_ACCESS_LEVELS,
  coursesNeedingALicense,
  enrollableCourseCount,
  groupsAt,
  participantsAt,
  participantsAtIncludingOutsiders,
  totalCourses,
  totalParticipants,
} from '../Selectors';
import { unrecognizedEmails } from '../ParticipantLists';
import { shareStore } from '../../Stores/ShareStore';
import AccessTabs, { emptyTabMessage } from './AccessTabs';
import LicenseGroup from './LicenseGroup';
import UnknownParticipants from './UnknownParticipants';

interface Props {
  alerts: LicenseAlerts;
  access: UnlicensedAccess;
}

// Open on the least licensed level that actually has people in it: that is the
// one the admin needs to decide about.
const initialLevel = (alerts: LicenseAlerts, unrecognizedCount: number): AccessLevel =>
  ALL_ACCESS_LEVELS.find(
    (level) => participantsAtIncludingOutsiders(alerts, level, unrecognizedCount) > 0,
  ) ?? 'full';

function LicenseGroups({ alerts, access }: Props) {
  const outsiderEmails = unrecognizedEmails(alerts, shareStore.participants);
  const outsiders = outsiderEmails.length;
  const [active, setActive] = useState<AccessLevel>(initialLevel(alerts, outsiders));
  const [openKeys, setOpenKeys] = useState<ReadonlyArray<string>>([]);

  const toggle = (key: string) => () =>
    setOpenKeys((keys) =>
      keys.includes(key) ? keys.filter((openKey) => openKey !== key) : [...keys, key],
    );

  const counts = ALL_ACCESS_LEVELS.reduce(
    (acc, level) => ({
      ...acc,
      [level]: participantsAtIncludingOutsiders(alerts, level, outsiders),
    }),
    {} as Record<AccessLevel, number>,
  );

  const unlicensedHere = participantsAt(alerts, 'none');

  // With one person in the list the row chip only repeats the active tab.
  const showsCoverageChip = totalParticipants(alerts) > 1;

  return (
    <div data-intercom-target="share-license-alert-groups">
      <Typography
        variant="caption"
        fontWeight="medium"
        className="mb-2 block tracking-wide text-muted-foreground"
      >
        <T kind="Participant licenses" />
      </Typography>

      <AccessTabs counts={counts} active={active} onSelect={setActive} />

      <div className="mt-3 flex flex-col gap-2.5">
        {/* Both kinds of "cannot enrol" live together, so the split is named. */}
        {active === 'none' && unlicensedHere > 0 && outsiders > 0 && (
          <Typography variant="bodyMedium" className="text-muted-foreground">
            <T
              kind="{{unlicensed}} with no license · {{outsiders}} not in your org"
              unlicensed={unlicensedHere}
              outsiders={outsiders}
            />
          </Typography>
        )}

        {fromEmpty([...groupsAt(alerts, active)]).cata({
          Nothing: () =>
            active === 'none' && outsiders > 0 ? (
              <UnknownParticipants emails={outsiderEmails} />
            ) : (
              <Typography variant="bodyMedium" className="py-4 text-muted-foreground">
                <T kind={emptyTabMessage(active)} />
              </Typography>
            ),
          Just: (groups) => (
            <>
              {groups.map((group) => (
                <LicenseGroup
                  key={group.key}
                  group={group}
                  coursesNeedingALicense={coursesNeedingALicense(alerts, group)}
                  enrollableCourseCount={enrollableCourseCount(alerts, group)}
                  totalCourseCount={totalCourses(alerts)}
                  licenseOptions={alerts.collection.licenseOptions}
                  unknownEmails={group.coverage === 'none' ? outsiderEmails : []}
                  access={access}
                  showsCoverageChip={showsCoverageChip}
                  open={openKeys.includes(group.key)}
                  onToggle={toggle(group.key)}
                />
              ))}
            </>
          ),
        })}
      </div>

      <Typography variant="bodyMedium" className="mt-3 block text-muted-foreground">
        <T
          kind="Assign licenses on the <peopleLink>People</peopleLink> page."
          peopleLink={(children) => (
            <Link
              to="/people/participants"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:underline"
              data-intercom-target="share-license-alert-people-link"
            >
              {children}
            </Link>
          )}
        />
      </Typography>
    </div>
  );
}

export default LicenseGroups;
